<script setup lang="ts">
import { useIdle, useTimestamp } from '@vueuse/core';

const { isAuthenticated } = storeToRefs(useAuthStore());
const { userLastLogin, userName } = useUserData();
const { timeLoggedIn } = storeToRefs(useUserActivityStore());
const { isCheckInactivityEnabled, isFooterV2Enabled } = useFeatureFlagsJson();
const { incrementTimeLoggedIn } = useUserActivityStore();
const layoutStore = useLayoutStore();
const { openModal } = layoutStore;
const isDarkTheme = computed(() => layoutStore.isDarkTheme);
const { isMobile } = useLayout();
const { loggedOutByInactivity } = storeToRefs(useUserActivityStore());
const { setLoggedOutByInactivity } = useUserActivityStore();
const { isCactusSportsbookActive } = useSportsBook();

const firstName = computed(() => userName.value.split(' ')[0]);

const MAX_INACTIVE_MINUTES = 28;
const SHOW_INACTIVE_SECONDS = 60 * 5;

const lastLoginDate = computed(() => {
  if (userLastLogin.value) {
    const date = new Date(userLastLogin.value);

    return date.toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  }

  return null;
});

const { idle, lastActive } = useIdle(MAX_INACTIVE_MINUTES * 60 * 1000);

const now = useTimestamp({ interval: 1000 });

const inactiveSeconds = computed(() => {
  return Math.floor((now.value - lastActive.value) / 1000);
});

const showInactiveTime = computed(() => {
  return inactiveSeconds.value >= SHOW_INACTIVE_SECONDS;
});

const hideTimeLoggedIn = computed(() => {
  return showInactiveTime.value && isMobile.value;
});

let increaseTime: NodeJS.Timeout | null = null;

const startIncrementTime = () => {
  if (!increaseTime) {
    increaseTime = setInterval(() => {
      incrementTimeLoggedIn();
      now.value = Date.now();
    }, 1000);
  }
};

const stopIncrementTime = () => {
  if (increaseTime) {
    clearInterval(increaseTime);
    increaseTime = null;
  }
};

// formatTime vem de utils/format-time.ts (auto-imported), compartilhado
// com components/footer/v2/SessionInfo.vue.

const loggoutAlertOpen = ref(true);

const closeLogoutAlert = () => {
  loggoutAlertOpen.value = false;

  setLoggedOutByInactivity(false);
};

const showLogoutAlert = computed(() => {
  return loggedOutByInactivity.value && loggoutAlertOpen.value;
});

function start() {
  if (isAuthenticated.value) {
    setTimeout(() => {
      startIncrementTime();
    }, 1000);
  } else {
    stopIncrementTime();
  }
}

onMounted(() => {
  start();
});

onActivated(() => {
  start();
});

onBeforeUnmount(() => {
  stopIncrementTime();
});

watch(
  () => isAuthenticated.value,
  (isAuthenticated) => {
    if (!isAuthenticated) {
      stopIncrementTime();
    } else {
      start();
    }
  },
  { immediate: true },
);

watch(idle, (idle) => {
  if (idle) {
    if (!isAuthenticated.value || !isCheckInactivityEnabled.value) {
      return;
    }

    openModal('check-activity');
  }
});
</script>

<template>
  <div
    v-if="isAuthenticated && !isFooterV2Enabled && !isCactusSportsbookActive"
    :class="[$style.loggedInfos, { 'bg-black': isDarkTheme }]"
  >
    <div :class="$style.content">
      <div
        v-if="firstName && !isMobile"
        :class="$style.welcome"
      >
        {{ $t('header_infos.welcome') }},
        <strong>{{ firstName }}</strong>
      </div>

      <span v-if="showInactiveTime">
        {{ $t('header_infos.inactive_time') }}
        <strong class="text-primary">
          {{ formatTime(inactiveSeconds) }}
        </strong>
      </span>

      <span v-if="!hideTimeLoggedIn">
        {{ $t('header_infos.time_logged_in') }}
        <strong>{{ formatTime(timeLoggedIn) }}</strong>
      </span>

      <span>
        {{ $t('header_infos.last_login') }}
        <strong>{{ lastLoginDate }}</strong>
      </span>
    </div>
  </div>

  <div
    v-if="showLogoutAlert && !isCactusSportsbookActive"
    :class="$style.loggedOut"
  >
    <div :class="$style.content">
      <span
        :class="$style.message"
        v-html="$t('header_infos.logged_out')"
      />

      <button
        :class="$style.close"
        @click.prevent="closeLogoutAlert"
      >
        <NuxtIcon name="close" />
      </button>
    </div>
  </div>
</template>

<style module lang="postcss">
.loggedInfos,
.loggedOut {
  @apply flex items-center px-3 md:px-4 py-2 md:py-2.5 bg-footer-bg-primary text-texts/60 text-2xs md:text-xs font-semibold whitespace-nowrap overflow-hidden h-8 md:h-10 opacity-80;

  @media print {
    & {
      display: none;
    }
  }

  .content {
    @apply w-full mx-auto max-w-[1140px] flex items-center justify-between md:justify-end md:px-[15px] lg:pr-4 lg:pl-2 gap-3 md:gap-4;

    .welcome {
      @apply mr-auto;
    }

    span {
      ~ span {
        @apply border-0 md:border-l border-solid border-l-header-links/10 pl-0 md:pl-4;
      }

      strong {
        -webkit-font-variant-numeric: tabular-nums;
        -moz-font-variant-numeric: tabular-nums;
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.02em;
      }
    }
  }
}

.loggedOut {
  @apply bg-error text-white;

  .message {
    @apply mr-auto border-0;
  }

  .close {
    @apply cursor-pointer p-3 -m-3;
  }
}
</style>
